import React, { useEffect, useState } from 'react';
import { fetchSecurityReports } from './api';
import type {
  ApiSecurityReport,
  RiskLevelFilter,
  StatusFilter
} from './types';
import { SecurityReportsTable } from './components/SecurityReportsTable';
import { StatusBadge } from './components/StatusBadge';
import { RiskLevelTag } from './components/RiskLevelTag';
import { useTheme } from './theme';

interface Filters {
  packageName: string;
  status: StatusFilter;
  riskLevel: RiskLevelFilter;
}

interface FiltersBarProps {
  filters: Filters;
  onChange: (next: Filters) => void;
}

function FiltersBar({ filters, onChange }: FiltersBarProps) {
  const handlePackageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, packageName: e.target.value });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, status: e.target.value as StatusFilter });
  };

  const handleRiskChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, riskLevel: e.target.value as RiskLevelFilter });
  };

  const handleReset = () => {
    onChange({ packageName: '', status: 'ALL', riskLevel: 'ALL' });
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white/80 px-4 py-3 shadow-sm dark:border-slate-700/70 dark:bg-slate-900/70 md:flex-row md:items-end md:justify-between">
      <div className="flex-1 space-y-2">
        <label className="block text-[11px] font-medium tracking-wide text-slate-600 dark:text-slate-400">
          Package name
        </label>
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs text-slate-400 dark:text-slate-500">
            🔍
          </span>
          <input
            type="text"
            placeholder="e.g. @malware/env-exfil-pkg"
            value={filters.packageName}
            onChange={handlePackageChange}
            className="block w-full rounded-lg border border-slate-300 bg-white/70 px-8 py-1.5 text-sm text-slate-900 shadow-sm outline-none ring-0 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-50 dark:placeholder:text-slate-500"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 md:flex-row md:justify-end">
        <div>
          <label className="block text-[11px] font-medium tracking-wide text-slate-600 dark:text-slate-400">
            Status
          </label>
          <select
            value={filters.status}
            onChange={handleStatusChange}
            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white/70 px-3 py-1.5 text-xs text-slate-900 shadow-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-50"
          >
            <option value="ALL">All</option>
            <option value="APPROVED">Approved</option>
            <option value="APPROVED_WITH_WARNINGS">Approved with warnings</option>
            <option value="BLOCKED">Blocked</option>
            <option value="PENDING">Pending</option>
            <option value="ERROR">Error</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium tracking-wide text-slate-600 dark:text-slate-400">
            Risk level
          </label>
          <select
            value={filters.riskLevel}
            onChange={handleRiskChange}
            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white/70 px-3 py-1.5 text-xs text-slate-900 shadow-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-50"
          >
            <option value="ALL">All</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </select>
        </div>
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center justify-center rounded-lg border border-slate-300/80 bg-transparent px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-100/80 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-slate-800/80"
        >
          Reset filters
        </button>
      </div>
    </div>
  );
}

function formatRange(page: number, pageSize: number, total: number): string {
  if (total === 0) return '0 of 0';
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return `${start}–${end} of ${total}`;
}

function App() {
  const { theme, toggleTheme } = useTheme();

  const [filters, setFilters] = useState<Filters>({
    packageName: '',
    status: 'ALL',
    riskLevel: 'ALL'
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [reports, setReports] = useState<ApiSecurityReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ApiSecurityReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchSecurityReports({
          page,
          pageSize,
          packageName: filters.packageName,
          status: filters.status,
          riskLevel: filters.riskLevel
        });
        if (cancelled) return;
        setReports(res.items);
        setTotal(res.total);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message ?? 'Unknown error');
        setReports([]);
        setTotal(0);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, filters.packageName, filters.status, filters.riskLevel]);

  useEffect(() => {
    if (!selected) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelected(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selected]);

  const handleFiltersChange = (next: Filters) => {
    setFilters(next);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handlePrev = () => {
    setPage((p) => Math.max(1, p - 1));
  };

  const handleNext = () => {
    setPage((p) => Math.min(totalPages, p + 1));
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
      <div className="border-b border-slate-200/80 bg-white/80 backdrop-blur-sm dark:border-slate-800/80 dark:bg-slate-950/90">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div>
            <div className="text-sm font-semibold tracking-wide text-emerald-600 dark:text-emerald-400">
              NoPackageMalware
            </div>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Because npm install shouldnt feel like Russian roulette.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300/80 bg-white/70 px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-100/80 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-100 dark:hover:bg-slate-800/80"
              aria-pressed={theme === 'dark'}
            >
              <span aria-hidden="true">{theme === 'dark' ? '🌙' : '☀️'}</span>
              <span>{theme === 'dark' ? 'Dark' : 'Light'} mode</span>
            </button>
            <a
              href="/request-access"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-emerald-50 shadow-sm transition hover:bg-emerald-500"
            >
              <span aria-hidden="true">🔑</span>
              <span>Request access</span>
            </a>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-5 sm:py-6">
        <header className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              Security reports
            </h1>
            <p className="mt-1 max-w-2xl text-xs text-slate-600 dark:text-slate-400">
              Browse and investigate automated security scans for npm packages before theyre allowed into your projects.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            <span className="rounded-full border border-slate-200 bg-white/70 px-2.5 py-1 text-[11px] font-medium shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
              {total} report{total === 1 ? '' : 's'}
            </span>
          </div>
        </header>

        <FiltersBar filters={filters} onChange={handleFiltersChange} />

        <SecurityReportsTable
          reports={reports}
          loading={loading}
          error={error}
          onRowClick={(report) => setSelected(report)}
        />

        <div className="flex items-center justify-between text-xs">
          <div className="rounded-lg border border-slate-200 bg-white/80 px-3 py-2 text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300">
            {formatRange(page, pageSize, total)}
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-2.5 py-1.5 text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300">
            <button
              type="button"
              onClick={handlePrev}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <span aria-hidden="true">«</span>
              <span>Prev</span>
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={handleNext}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <span>Next</span>
              <span aria-hidden="true">»</span>
            </button>
          </div>
        </div>

        {selected && (() => {
          const details: any = selected.details || null;
          const filesAuditedObj = details?.filesAudited;
          const filesAudited: string[] | undefined = Array.isArray(filesAuditedObj?.files)
            ? filesAuditedObj.files
            : Array.isArray(filesAuditedObj)
              ? filesAuditedObj
              : undefined;
          const filesTruncated = !!filesAuditedObj?.truncated;
          const filesCount = filesAudited?.length ?? 0;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
              <div
                className="absolute inset-0"
                onClick={() => setSelected(null)}
              />

              <section
                className="relative z-10 w-full max-w-2xl rounded-xl border border-slate-200 bg-white/95 px-5 py-4 text-xs text-slate-800 shadow-2xl dark:border-slate-700/70 dark:bg-slate-900/95 dark:text-slate-200"
                role="dialog"
                aria-modal="true"
                aria-label={`${selected.packageName}@${selected.version} security details`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2">
                    <div>
                      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                        {selected.packageName}@{selected.version}
                      </h2>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <StatusBadge status={selected.status} />
                        <RiskLevelTag level={selected.riskLevel} />
                      </div>
                    </div>
                    <p className="mt-1 leading-relaxed text-slate-700 dark:text-slate-200">
                      {selected.summary}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="ml-4 inline-flex h-7 aspect-square items-center justify-center rounded-full text-xs leading-none text-slate-400 transition hover:bg-slate-200/70 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:text-slate-300 dark:hover:bg-slate-800/80"
                    aria-label="Close details"
                  >
                    ✕
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] text-slate-600 dark:text-slate-300">
                  <div>
                    <div className="font-semibold text-slate-700 dark:text-slate-200">Risk score</div>
                    <div className="mt-0.5 text-sm text-slate-900 dark:text-slate-50">
                      {selected.riskScore}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700 dark:text-slate-200">Last scan</div>
                    <div className="mt-0.5 text-xs">
                      {selected.lastScanAt}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700 dark:text-slate-200">Files audited</div>
                    <div className="mt-0.5 text-xs">
                      {filesCount}
                      {filesTruncated ? ' (truncated)' : ''}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700 dark:text-slate-200">Status</div>
                    <div className="mt-0.5 text-xs">
                      <StatusBadge status={selected.status} />
                    </div>
                  </div>
                </div>

                {filesAudited && filesAudited.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                      Audited files ({filesAudited.length}
                      {filesTruncated ? ' – truncated' : ''})
                    </div>
                    <ul className="mt-1 max-h-40 overflow-auto rounded-md bg-slate-50/80 p-2 text-[10px] text-slate-800 shadow-inner dark:bg-slate-950/70 dark:text-slate-200">
                      {filesAudited.map((f) => (
                        <li key={f} className="font-mono break-all">
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {details && (
                  <details className="mt-3 rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2 text-[11px] text-slate-800 dark:border-slate-700 dark:bg-slate-950/70 dark:text-slate-100">
                    <summary className="cursor-pointer font-semibold text-slate-700 dark:text-slate-200">
                      Raw investigation details
                    </summary>
                    <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-[10px]">
                      {JSON.stringify(details, null, 2)}
                    </pre>
                  </details>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                  >
                    Close
                  </button>
                </div>
              </section>
            </div>
          );
        })()}
      </main>
    </div>
  );
}

export default App;
